import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useCurrencyField } from '@vaptmenu/money';
import { api, describeCommercialError, describeError, newIdempotencyKey } from '@/lib/api';
import { useIdempotencyKey } from '@/lib/idempotency';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';
import { LimitOverrideCheckbox } from '@/components/LimitOverrideCheckbox';
import { useHasPlatformPermission } from '@/stores/auth';

interface Adjustment {
  id: string;
  tenantId: string;
  amount: number;
  type: string;
  status: string;
  asaasAction: string;
  asaasPaymentId: string | null;
  asaasErrorMessage: string | null;
  reason: string;
  createdAt: string;
  appliedAt: string | null;
  reconciledAt: string | null;
  // C13 (N6 e N7): a intenção do crédito e a marca de conciliação. Opcionais para a API anterior,
  // que não os manda.
  asaasValueBefore?: number | null;
  asaasValueAfter?: number | null;
  needsReconciliation?: boolean;
}

/** A fatura que o próximo crédito abate, com o valor ao vivo no Asaas (`GET .../credit-target`). */
interface CreditTarget {
  invoiceId: string | null;
  dueDate: string | null;
  localAmount: number | null;
  liveAmount: number | null;
  liveStatus: string | null;
  liveUnavailable: boolean;
}

const formatBrl = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** `YYYY-MM-DD` como data local: `new Date('2026-10-20')` cairia no dia anterior em UTC-3. */
const formatDueDate = (date: string) => {
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
};

/** O que a tela diz sobre a fatura alvo do crédito, antes do envio. */
function describeCreditTarget(target: CreditTarget | null): string {
  if (!target) return 'Fatura alvo: carregando…';
  if (!target.invoiceId || !target.dueDate) return 'Nenhuma fatura pendente: o crédito ficará como falho, sem chegar ao Asaas.';
  const due = formatDueDate(target.dueDate);
  if (target.liveUnavailable || target.liveAmount === null) {
    return `Fatura alvo: vence em ${due}. Valor no Asaas indisponível agora; o crédito confere de novo no envio.`;
  }
  return `Fatura alvo: vence em ${due}, ${formatBrl(target.liveAmount)} no Asaas.`;
}

interface Page<T> {
  items: T[];
  page: number;
  totalPages: number;
}

const statusBadge = (s: string) => {
  switch (s) {
    case 'AppliedToAsaas': return 'badge badge-success';
    case 'Reconciled':
    case 'MarkedReconciled': return 'badge badge-info';
    case 'Failed': return 'badge badge-error';
    default: return 'badge';
  }
};

export function AdjustmentsPage() {
  const { tenantId } = useParams();
  // Criar e marcar reconciliado exigem platform.adjustments.write na API; a
  // listagem é de todo operador (change platform-backoffice-hardening, C1).
  const canWrite = useHasPlatformPermission('platform.adjustments.write');
  const [items, setItems] = useState<Adjustment[]>([]);
  // `loading` e derivado: guardamos qual busca ja terminou e comparamos com a
  // que deveria estar na tela. Pedir recarga muda a chave, e a tela volta a
  // "carregando" no mesmo render — sem escrever estado dentro do efeito.
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshSeq, setRefreshSeq] = useState(0);

  // create form
  // Número, não texto. O campo livre aceitava "2555" e mandava R$ 2.555,00
  // para o Asaas sem que ninguém na tela visse a diferença entre isso e
  // R$ 25,55. Com a máscara, o que o operador lê é o que será lançado.
  const [amount, setAmount] = useState<number | null>(null);
  const [type, setType] = useState<'Credit' | 'InternalVoucher'>('Credit');
  const [reason, setReason] = useState('');
  const [limitOverride, setLimitOverride] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const idempotency = useIdempotencyKey();
  const [creditTarget, setCreditTarget] = useState<CreditTarget | null>(null);

  const amountField = useCurrencyField({ value: amount, onValueChange: setAmount });

  const requestKey = `${tenantId}:${refreshSeq}`;
  const loading = loadedKey !== requestKey;

  // A fatura que o crédito vai abater, lida ao vivo no Asaas. A tela a mostra e devolve no POST
  // (expectedInvoiceId e expectedInvoiceAmount): se ela mudou desde então, a API responde 409 em
  // vez de abater outro valor. Só quem pode criar o crédito consulta (a rota pede a permissão).
  useEffect(() => {
    if (!tenantId || !canWrite) return;
    let cancelled = false;
    api.get<CreditTarget>(`/platform/tenants/${tenantId}/adjustments/credit-target`)
      .then(({ data }) => !cancelled && setCreditTarget(data && 'invoiceId' in data ? data : null))
      .catch(() => !cancelled && setCreditTarget(null));
    return () => { cancelled = true; };
  }, [tenantId, canWrite, refreshSeq]);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    // `page` nao tem default no endpoint: omitir devolve 500 e a tela nunca
    // carrega. Era o estado desta pagina ate a suite @backoffice pegar.
    api.get<Page<Adjustment>>(`/platform/tenants/${tenantId}/adjustments`, { params: { page: 1, pageSize: 50 } })
      .then(({ data }) => !cancelled && setItems(data.items))
      .catch((err) => !cancelled && setError(describeError(err)))
      .finally(() => !cancelled && setLoadedKey(requestKey));
    return () => { cancelled = true; };
  }, [tenantId, refreshSeq, requestKey]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    const url = `/platform/tenants/${tenantId}/adjustments`;
    // Só a exceção do Admin leva a marca; sem ela, o campo nem vai. O crédito leva a fatura e o
    // valor que a tela mostrou (N7). A fatura vai sempre que a tela mostrou uma (o vencimento
    // aparece mesmo com o Asaas fora); o valor, só quando foi lido. Sem fatura lida, nenhum dos
    // dois vai e a API confere sozinha.
    const shownInvoice = type === 'Credit' && creditTarget?.invoiceId
      ? {
          expectedInvoiceId: creditTarget.invoiceId,
          ...(creditTarget.liveAmount !== null ? { expectedInvoiceAmount: creditTarget.liveAmount } : {}),
        }
      : {};
    const body = {
      amount,
      type,
      reason: reason.trim(),
      ...(limitOverride ? { limitOverride: true } : {}),
      ...shownInvoice,
    };
    try {
      // A mesma chave na repetição do mesmo pedido: depois de um timeout, o segundo clique recebe
      // o desfecho do primeiro em vez de lançar outro crédito.
      await api.post(url, body, { headers: { 'Idempotency-Key': idempotency.keyFor(url, body) } });
      idempotency.settle();
      setAmount(null);
      setReason('');
      setLimitOverride(false);
      setRefreshSeq((s) => s + 1);
    } catch (err) {
      idempotency.settle(err);
      setFormError(describeCommercialError(err, 'currency'));
    } finally {
      setSubmitting(false);
    }
  }

  async function markReconciled(id: string) {
    if (!confirm('Marcar este ajuste como reconciliado manualmente?')) return;
    try {
      await api.post(
        `/platform/tenants/${tenantId}/adjustments/${id}/mark-reconciled`,
        {},
        { headers: { 'Idempotency-Key': newIdempotencyKey() } },
      );
      setRefreshSeq((s) => s + 1);
    } catch (err) {
      setError(describeError(err));
    }
  }

  return (
    <div className="page page-detail">
      <Link to={`/tenants/${tenantId}`} className="backlink">← Tenant</Link>
      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
        Concessões comerciais
      </h2>
      <p className="muted" style={{ margin: 0 }}>
        <strong>Crédito</strong> aplica desconto na próxima fatura via Asaas.{' '}
        <strong>Voucher interno</strong> registra para relatórios internos sem
        afetar a cobrança do tenant.
      </p>

      {canWrite && (
        <form className="card" onSubmit={onCreate} style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 16 }}>Nova concessão</h3>
          {formError && <div className="error-banner">{formError}</div>}
          <div className="detail-grid">
            <div className="field">
              <label htmlFor="amount">Valor (R$)</label>
              <input id="amount" placeholder="0,00" required {...amountField} />
            </div>
            <div className="field">
              <label htmlFor="type">Tipo</label>
              <select id="type" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
                <option value="Credit">Credit (aplica no Asaas)</option>
                <option value="InternalVoucher">InternalVoucher (só registro)</option>
              </select>
            </div>
          </div>
          {type === 'Credit' && (
            <p className="muted" data-testid="credit-target" style={{ margin: '0 0 8px' }}>
              {describeCreditTarget(creditTarget)}
            </p>
          )}
          <div className="field">
            <label htmlFor="reason">Motivo</label>
            <textarea
              id="reason" rows={2} required minLength={3}
              value={reason} onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <LimitOverrideCheckbox checked={limitOverride} onChange={setLimitOverride} />
          <button type="submit" className="btn" disabled={submitting}>
            {submitting ? 'Criando…' : 'Criar concessão'}
          </button>
        </form>
      )}

      {error && <div className="error-banner">{error}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-scroll">
          <CardTable>
            <THead>
              <Tr>
                <Th>Quando</Th><Th>Tipo</Th><Th>Valor</Th><Th>Status</Th>
                <Th>Motivo</Th><Th>Asaas</Th><Th></Th>
              </Tr>
            </THead>
            <TBody>
              {loading && <Tr><Td colSpan={7} style={{ textAlign: 'center', padding: 24 }} className="muted">Carregando…</Td></Tr>}
              {!loading && items.length === 0 && (
                <Tr><Td colSpan={7} style={{ textAlign: 'center', padding: 24 }} className="muted">Sem concessões neste tenant.</Td></Tr>
              )}
              {!loading && items.map((a) => (
                <Tr key={a.id}>
                  <Td heading>{new Date(a.createdAt).toLocaleString('pt-BR')}</Td>
                  <Td label="Tipo"><span className="badge">{a.type}</span></Td>
                  <Td label="Valor">R$ {a.amount.toFixed(2)}</Td>
                  <Td label="Status"><span className={statusBadge(a.status)}>{a.status}</span></Td>
                  <Td label="Motivo" style={{ maxWidth: 240 }}>{a.reason}</Td>
                  <Td label="Asaas">
                    {a.asaasPaymentId && <span className="mono" style={{ fontSize: 12 }}>{a.asaasPaymentId}</span>}
                    {a.asaasErrorMessage && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                        {a.asaasErrorMessage}
                      </div>
                    )}
                    {a.needsReconciliation && a.status === 'Failed' && (
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        <span className="badge badge-error">Conferir no Asaas</span>{' '}
                        {a.asaasValueBefore != null && a.asaasValueAfter != null && (
                          <span className="muted">
                            {`não aplicado: ${formatBrl(a.asaasValueBefore)} · aplicado: ${formatBrl(a.asaasValueAfter)}`}
                          </span>
                        )}
                      </div>
                    )}
                  </Td>
                  <Td action>
                    {canWrite && a.status === 'Failed' && (
                      <button className="btn-secondary btn" style={{ padding: '6px 10px', fontSize: 12 }} onClick={() => markReconciled(a.id)}>
                        Marcar reconciliado
                      </button>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </CardTable>
        </div>
      </div>
    </div>
  );
}
